import { useMemo } from 'react'

import { useAuth } from '@/auth/use-auth'
import { PERMISSIONS } from '@/lib/permissions'

import { APPROVE_SUFFIX, checkerPermissionFor, isPermissionCode } from './request-types'

/**
 * What this user may do in the approvals area.
 *
 * The inbox itself carries no permission of its own — that is deliberate on the server, and it is
 * the reason this screen is reachable from every menu. A user with no countersigning permission
 * gets an empty list rather than a refusal, because "there is nothing for you to approve" is the
 * correct answer to the question, not an error.
 *
 * Everything below is presentation. The engine re-checks the permission for the *specific request
 * type* on every approve and reject, so a row that renders an Approve button is not an open door
 * and a row without one is not a closed one.
 */
export const APPROVAL_PERMISSIONS = {
  /** The audit trail, which is the only history of a request that exists. */
  trailView: PERMISSIONS.ops.auditView,
  /** The staff directory, read for one fact: whether the maker is still active. */
  staffView: PERMISSIONS.admin.userView,
} as const

export interface ApprovalPermissions {
  canViewTrail: boolean
  canViewStaff: boolean
  /**
   * The operations this user holds the countersigning half of, as request types — every held
   * permission ending in `.approve` whose stripped form is itself a permission code.
   *
   * The second half of that test is not a nicety. `customers.kyc.approve` and `loans.approve` are
   * *maker* permissions that happen to end in the suffix, which is why the catalogue names their
   * checker halves `customers.kyc.approve.approve` and `loans.approve.approve`. Stripping blindly
   * would tell a KYC officer they may countersign `customers.kyc` and a credit officer that they
   * may countersign `loans` — neither of which is an operation.
   *
   * That is exactly the set the server filters the inbox by, so it is also the honest answer to
   * "why is my queue empty": either nobody has raised one of these, or you hold none of them.
   */
  approvableRequestTypes: string[]
  /** Whether this user could countersign the given request type. */
  canApprove: (requestType: string) => boolean
}

export function useApprovalPermissions(): ApprovalPermissions {
  const { user } = useAuth()
  const permissions = user?.permissions

  return useMemo(() => {
    const held = new Set<string>(permissions ?? [])

    return {
      canViewTrail: held.has(APPROVAL_PERMISSIONS.trailView),
      canViewStaff: held.has(APPROVAL_PERMISSIONS.staffView),
      approvableRequestTypes: [...held]
        .filter((code) => code.endsWith(APPROVE_SUFFIX))
        .map((code) => code.slice(0, -APPROVE_SUFFIX.length))
        .filter(isPermissionCode)
        .sort((left, right) => left.localeCompare(right)),
      canApprove: (requestType: string) => held.has(checkerPermissionFor(requestType)),
    }
  }, [permissions])
}
